import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { EditOutlined, EyeOutlined, FormOutlined, ProfileOutlined, PlusOutlined, UserAddOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType, FirmGetAllResponse } from '@pmp/api';
import { Guard, useGuard } from '@ohpen/react-authz';

import { columns } from '../tableConfig';
import { FirmsListProps } from '../interfaces';
import { lockActionTitle } from '@pmp/utils';

const entityType = ActionEntityType.LENDER_FIRMS;

export const FirmsList = memo(
  ({
    isFetching,
    data,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onOpenInPanel,
    onSeeFirmCases,
    addIntermediary,
    valueLists,
  }: FirmsListProps<FirmGetAllResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    const getRowActions: (record: FirmGetAllResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View firms" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit firms"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['firms:delete']} flags={['enableFirmsCrud']}>
            <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete firms"
                icon={<DeleteOutlined key="delete" rev={undefined} />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Tooltip key="panel" title={t('lender.firms.labels.openInPanel')} placement="top">
            <Button type="text" aria-label="Open in Panel" icon={<FormOutlined key="edit" />} onClick={() => onOpenInPanel(record)} />
          </Tooltip>,
          <Tooltip key="cases" title={t('lender.firms.labels.seeFirmCases')} placement="top">
            <Button type="text" aria-label="See Firm Cases" icon={<ProfileOutlined key="edit" />} onClick={() => onSeeFirmCases(record)} />
          </Tooltip>,
          <Guard key="inviteIntermediary" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="inviteIntermediary" title={t('lender.firms.labels.inviteIntermediary')} placement="top">
              <Button
                type="text"
                aria-label="Invite Intermediary"
                icon={<UserAddOutlined key="edit" />}
                onClick={() => addIntermediary(record)}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [t, onView, onEdit, onOpenInPanel, onSeeFirmCases, addIntermediary, onDelete],
    );

    const processedData = useMemo((): FirmGetAllResponse[] => {
      if (!data) return [];
      const principals = valueLists?.[FieldType.PRINCIPALS];
      if (!principals) return data;
      const nameToId: Record<string, string> = Object.fromEntries(
        Object.entries(principals).map(([id, name]) => [name, id]),
      );
      return data.map(firm => ({
        ...firm,
        principalFirm: firm.principalFirm.map(ref => ({
          key: nameToId[ref.name ?? ''] ?? '',
          value: ref.name ?? '',
        })),
      })) as unknown as FirmGetAllResponse[];
    }, [data, valueLists]);

    const tableProps: TableProps<ActionEntityType, FirmGetAllResponse> = useMemo(
      () => ({
        id: entityType,
        ref: tableRef,
        dataSource: processedData,
        columns,
        rowKey: 'ohpenId',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        loading: isFetching,
        showRowSelection,
        getRowActions,
        showViews: true,
        valueLists,
      }),
      [getRowActions, valueLists, processedData, isFetching, showRowSelection],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Table.ExportButton
                fileName={`Firms ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Firms"
                disabled={isFetching}
                exportView
              />
              <Guard permissions={['firms:create']} flags={['enableFirmsCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('global.labels.create')}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="lender.firms.labels.xFirmsSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                fileName={`Firms ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Firms"
                disabled={isFetching}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
